/**
 * GCS Resumable Upload Client
 * Implements Google Cloud Storage resumable upload protocol for direct browser-to-GCS uploads.
 *
 * Reference: https://cloud.google.com/storage/docs/resumable-uploads
 */

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
const STORAGE_KEY_PREFIX = 'video_upload_state_';

class GcsUploader extends EventTarget {
    constructor(options = {}) {
        super();
        this.chunkSize = options.chunkSize || CHUNK_SIZE;
        this.file = null;
        this.uploadUrl = null;
        this.sessionUri = null;
        this.uploadId = null;
        this.bytesUploaded = 0;
        this.isPaused = false;
        this.abortController = null;
        this.startTime = null;
        this.lastProgressTime = null;
        this.lastProgressBytes = 0;
    }

    /**
     * Initialize resumable upload session with GCS
     */
    async initUpload(file, signedUrl, uploadId) {
        this.file = file;
        this.uploadUrl = signedUrl;
        this.uploadId = uploadId;
        this.startTime = Date.now();
        this.lastProgressTime = this.startTime;

        try {
            // Step 1: Initiate resumable upload session with GCS
            const response = await fetch(signedUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': file.type,
                    'Content-Length': '0',
                    'x-goog-resumable': 'start',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to initiate upload: ${response.status} ${errorText}`);
            }

            // Get the session URI from the Location header
            this.sessionUri = response.headers.get('Location');

            if (!this.sessionUri) {
                throw new Error('No session URI returned from GCS');
            }

            this.saveState();

            return this.sessionUri;
        } catch (error) {
            console.error('Init upload error:', error);
            throw error;
        }
    }

    /**
     * Upload file in chunks using resumable upload protocol
     */
    async upload() {
        if (!this.sessionUri) {
            throw new Error('Upload not initialized. Call initUpload() first.');
        }

        this.isPaused = false;
        this.abortController = new AbortController();

        try {
            while (this.bytesUploaded < this.file.size && !this.isPaused) {
                const chunk = this.file.slice(
                    this.bytesUploaded,
                    Math.min(this.bytesUploaded + this.chunkSize, this.file.size)
                );

                const startByte = this.bytesUploaded;
                const endByte = this.bytesUploaded + chunk.size - 1;
                const contentRange = `bytes ${startByte}-${endByte}/${this.file.size}`;

                const response = await fetch(this.sessionUri, {
                    method: 'PUT',
                    headers: {
                        'Content-Length': chunk.size.toString(),
                        'Content-Range': contentRange,
                    },
                    body: chunk,
                    signal: this.abortController.signal,
                });

                if (response.status === 308) {
                    // Incomplete - parse Range header for next offset
                    const range = response.headers.get('Range');
                    if (range) {
                        const match = range.match(/bytes=0-(\d+)/);
                        if (match) {
                            this.bytesUploaded = parseInt(match[1], 10) + 1;
                        }
                    } else {
                        // If no Range header, assume chunk was accepted
                        this.bytesUploaded = endByte + 1;
                    }
                } else if (response.status === 200 || response.status === 201) {
                    // Upload complete
                    this.bytesUploaded = this.file.size;
                } else {
                    const errorText = await response.text();
                    throw new Error(`Upload failed: ${response.status} ${errorText}`);
                }

                this.saveState();
                this.emitProgress();
            }

            if (this.bytesUploaded >= this.file.size) {
                this.clearState();
                this.dispatchEvent(new CustomEvent('complete', {
                    detail: { uploadId: this.uploadId }
                }));
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                // Paused or cancelled - not an error
                return;
            }

            console.error('Upload error:', error);
            this.dispatchEvent(new CustomEvent('error', {
                detail: { error: error.message }
            }));
            throw error;
        }
    }

    /**
     * Pause the upload
     */
    pause() {
        this.isPaused = true;
        this.abortController?.abort();
        this.saveState();
        this.dispatchEvent(new CustomEvent('paused'));
    }

    /**
     * Resume a paused upload
     */
    async resume() {
        if (!this.sessionUri) {
            throw new Error('No session URI available for resume');
        }

        // Query GCS for current upload status
        try {
            const response = await fetch(this.sessionUri, {
                method: 'PUT',
                headers: {
                    'Content-Length': '0',
                    'Content-Range': `bytes */${this.file.size}`,
                },
            });

            if (response.status === 308) {
                // Parse Range header to get current progress
                const range = response.headers.get('Range');
                if (range) {
                    const match = range.match(/bytes=0-(\d+)/);
                    if (match) {
                        this.bytesUploaded = parseInt(match[1], 10) + 1;
                    }
                }
            } else if (response.status === 200 || response.status === 201) {
                // Already complete
                this.bytesUploaded = this.file.size;
                this.dispatchEvent(new CustomEvent('complete', {
                    detail: { uploadId: this.uploadId }
                }));
                return;
            }

            // Resume upload
            await this.upload();
        } catch (error) {
            console.error('Resume error:', error);
            this.dispatchEvent(new CustomEvent('error', {
                detail: { error: error.message }
            }));
            throw error;
        }
    }

    /**
     * Cancel the upload
     */
    cancel() {
        this.isPaused = true;
        this.abortController?.abort();
        this.clearState();
        this.dispatchEvent(new CustomEvent('cancelled'));
    }

    /**
     * Emit progress event with statistics
     */
    emitProgress() {
        const now = Date.now();
        const percent = (this.bytesUploaded / this.file.size) * 100;

        // Calculate upload speed (bytes per second)
        const timeDelta = (now - this.lastProgressTime) / 1000; // seconds
        const bytesDelta = this.bytesUploaded - this.lastProgressBytes;
        const speed = timeDelta > 0 ? bytesDelta / timeDelta : 0;

        // Calculate time remaining
        const bytesRemaining = this.file.size - this.bytesUploaded;
        const timeRemaining = speed > 0 ? bytesRemaining / speed : 0;

        this.lastProgressTime = now;
        this.lastProgressBytes = this.bytesUploaded;

        this.dispatchEvent(new CustomEvent('progress', {
            detail: {
                bytesUploaded: this.bytesUploaded,
                totalBytes: this.file.size,
                percent: percent.toFixed(2),
                speed: speed,
                timeRemaining: timeRemaining,
            },
        }));
    }

    /**
     * Save upload state to localStorage for resume support
     */
    saveState() {
        if (!this.uploadId) return;

        const state = {
            uploadId: this.uploadId,
            sessionUri: this.sessionUri,
            bytesUploaded: this.bytesUploaded,
            fileSize: this.file?.size,
            fileName: this.file?.name,
            fileType: this.file?.type,
            timestamp: Date.now(),
        };

        localStorage.setItem(STORAGE_KEY_PREFIX + this.uploadId, JSON.stringify(state));
    }

    /**
     * Load upload state from localStorage
     */
    static loadState(uploadId) {
        const state = localStorage.getItem(STORAGE_KEY_PREFIX + uploadId);
        if (state) {
            try {
                const parsed = JSON.parse(state);
                // Only restore if less than 30 minutes old (matching signed URL expiry)
                if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
                    return parsed;
                }
            } catch (e) {
                console.error('Failed to parse upload state:', e);
            }
        }
        return null;
    }

    /**
     * Clear upload state from localStorage
     */
    clearState() {
        if (this.uploadId) {
            localStorage.removeItem(STORAGE_KEY_PREFIX + this.uploadId);
        }
    }
}

/**
 * Alpine.js component for video upload UI
 */
window.videoUploader = function(ragId) {
    return {
        // State
        file: null,
        uploading: false,
        paused: false,
        progress: 0,
        bytesUploaded: 0,
        totalBytes: 0,
        uploadSpeed: 0,
        timeRemaining: 0,
        error: null,
        uploadId: null,
        uploader: null,

        // File selection
        async selectFile(event) {
            const selectedFile = event.target.files[0];
            if (!selectedFile) return;

            // Client-side validation
            const allowedTypes = ['video/mp4', 'video/webm'];
            if (!allowedTypes.includes(selectedFile.type)) {
                this.error = 'Only MP4 and WebM files are supported for direct upload.';
                event.target.value = ''; // Clear input
                return;
            }

            // Check file size (500MB for free tier)
            const maxSize = 500 * 1024 * 1024; // 500MB
            if (selectedFile.size > maxSize) {
                this.error = `File size exceeds 500MB limit. Your file is ${this.formatFileSize(selectedFile.size)}.`;
                event.target.value = ''; // Clear input
                return;
            }

            this.file = selectedFile;
            this.totalBytes = selectedFile.size;
            this.error = null;

            // Auto-start upload
            await this.startUpload();
        },

        // Start upload
        async startUpload() {
            if (!this.file) return;

            this.uploading = true;
            this.error = null;
            this.progress = 0;
            this.bytesUploaded = 0;

            try {
                // 1. Get signed URL from backend
                const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content;
                if (!csrfToken) {
                    throw new Error('CSRF token not found');
                }

                const initResponse = await fetch('/api/video-upload/init', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                        filename: this.file.name,
                        mime_type: this.file.type,
                        file_size: this.file.size,
                        rag_id: ragId,
                    }),
                });

                if (!initResponse.ok) {
                    const errorData = await initResponse.json();
                    throw new Error(errorData.message || 'Failed to initialize upload');
                }

                const { upload_url, upload_id } = await initResponse.json();
                this.uploadId = upload_id;

                // 2. Initialize GCS uploader
                this.uploader = new GcsUploader();

                this.uploader.addEventListener('progress', (e) => {
                    this.bytesUploaded = e.detail.bytesUploaded;
                    this.progress = parseFloat(e.detail.percent);
                    this.uploadSpeed = e.detail.speed;
                    this.timeRemaining = e.detail.timeRemaining;
                });

                this.uploader.addEventListener('complete', async (e) => {
                    // 3. Notify backend of completion
                    await this.completeUpload(e.detail.uploadId);
                });

                this.uploader.addEventListener('error', (e) => {
                    this.error = e.detail.error;
                    this.uploading = false;
                });

                this.uploader.addEventListener('paused', () => {
                    this.paused = true;
                });

                this.uploader.addEventListener('cancelled', () => {
                    this.uploading = false;
                    this.paused = false;
                    this.progress = 0;
                    this.file = null;
                });

                // 3. Initialize and start upload
                await this.uploader.initUpload(this.file, upload_url, upload_id);
                await this.uploader.upload();

            } catch (error) {
                console.error('Upload error:', error);
                this.error = error.message;
                this.uploading = false;

                // Cancel on backend if we got an upload_id
                if (this.uploadId) {
                    await this.cancelUpload();
                }
            }
        },

        // Complete upload
        async completeUpload(uploadId) {
            try {
                const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content;

                const response = await fetch('/api/video-upload/complete', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({ upload_id: uploadId }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to complete upload');
                }

                const result = await response.json();

                // Success - dispatch event to refresh UI
                this.uploading = false;
                this.progress = 100;

                // Show success message
                window.dispatchEvent(new CustomEvent('video-uploaded', {
                    detail: { videoUuid: result.video_uuid }
                }));

                // Reset form after a brief delay
                setTimeout(() => {
                    this.resetUpload();
                }, 2000);

            } catch (error) {
                console.error('Complete upload error:', error);
                this.error = error.message;
                this.uploading = false;
            }
        },

        // Pause upload
        pauseUpload() {
            if (this.uploader) {
                this.uploader.pause();
            }
        },

        // Resume upload
        async resumeUpload() {
            if (this.uploader) {
                this.paused = false;
                try {
                    await this.uploader.resume();
                } catch (error) {
                    console.error('Resume error:', error);
                    this.error = error.message;
                    this.uploading = false;
                }
            }
        },

        // Cancel upload
        async cancelUpload() {
            if (this.uploader) {
                this.uploader.cancel();
            }

            // Notify backend to clean up
            if (this.uploadId) {
                try {
                    const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content;

                    await fetch('/api/video-upload/cancel', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': csrfToken,
                        },
                        credentials: 'same-origin',
                        body: JSON.stringify({ upload_id: this.uploadId }),
                    });
                } catch (error) {
                    console.error('Cancel upload error:', error);
                }
            }

            this.resetUpload();
        },

        // Reset upload state
        resetUpload() {
            this.file = null;
            this.uploading = false;
            this.paused = false;
            this.progress = 0;
            this.bytesUploaded = 0;
            this.totalBytes = 0;
            this.uploadSpeed = 0;
            this.timeRemaining = 0;
            this.error = null;
            this.uploadId = null;
            this.uploader = null;

            // Reset file input
            const fileInput = this.$refs.fileInput;
            if (fileInput) {
                fileInput.value = '';
            }
        },

        // Format file size for display
        formatFileSize(bytes) {
            if (bytes >= 1073741824) {
                return (bytes / 1073741824).toFixed(2) + ' GB';
            } else if (bytes >= 1048576) {
                return (bytes / 1048576).toFixed(2) + ' MB';
            } else if (bytes >= 1024) {
                return (bytes / 1024).toFixed(2) + ' KB';
            }
            return bytes + ' bytes';
        },

        // Format upload speed
        formatSpeed(bytesPerSecond) {
            return this.formatFileSize(bytesPerSecond) + '/s';
        },

        // Format time remaining
        formatTimeRemaining(seconds) {
            if (seconds < 60) {
                return Math.round(seconds) + 's';
            } else if (seconds < 3600) {
                return Math.floor(seconds / 60) + 'm ' + Math.round(seconds % 60) + 's';
            } else {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                return hours + 'h ' + minutes + 'm';
            }
        },
    };
};

// Export for use in modules
export { GcsUploader };
